import type { Card } from '@braze/web-sdk';
import { track } from '@eventbrite/datalayer-library';
import { getBrazeInstance } from '../client';
import { mountBrazeContentCardsUI } from '../ui/NotificationUIManager';

interface ExtendedCard extends Card {
    dismissed?: boolean;
}

export async function getContentCards(
    setIsDrawerOpen: (open: boolean) => void,
): Promise<void> {
    try {
        track({
            eventName: 'NotificationBellIconClicked',
            eventData: {},
        });

        const braze = await getBrazeInstance();

        await braze.requestContentCardsRefresh();
        await new Promise((resolve) => setTimeout(resolve, 100));

        const cachedCards: Card[] | null =
            braze.getCachedContentCards()?.cards || [];
        mountBrazeContentCardsUI(cachedCards, setIsDrawerOpen);

        braze.subscribeToContentCardsUpdates(() => {
            const updatedCards: Card[] | null =
                braze.getCachedContentCards()?.cards || [];
            mountBrazeContentCardsUI(updatedCards, setIsDrawerOpen);
        });

        braze.showContentCards();

        track({
            eventName: 'NotificationDrawerOpened',
            eventData: {},
        });

        setIsDrawerOpen(true);
    } catch (error) {
        console.error('[Braze] Error fetching content cards:', error);
        mountBrazeContentCardsUI([], setIsDrawerOpen); // render no cards message
    }
}

export async function hideContentCards(
    setIsDrawerOpen: (open: boolean) => void,
): Promise<void> {
    try {
        track({
            eventName: 'NotificationBellIconClicked',
            eventData: {},
        });
        const braze = await getBrazeInstance();
        braze.hideContentCards();
        setIsDrawerOpen(false);
        track({ eventName: 'NotificationDrawerCloseClicked', eventData: {} });
    } catch (e) {
        console.error('[Braze] Failed to hide Braze content cards:', e);
    }
}

export async function hasUnseenContentCards(): Promise<boolean> {
    try {
        const braze = await getBrazeInstance();
        if (!braze) {
            throw new Error(
                '[Braze] SDK not initialized. Call initBraze() first.',
            );
        }

        const cachedCards = braze.getCachedContentCards()
            .cards as ExtendedCard[];
        const hasUnseenInCache = cachedCards.some(
            (card) => !card.viewed && !card.dismissed,
        );

        // Request latest content cards
        await braze.requestContentCardsRefresh();

        return new Promise((resolve) => {
            let resolved = false;

            const fallbackTimeout = setTimeout(() => {
                if (!resolved) {
                    console.warn(
                        '[Braze] ContentCards update timed out. Falling back to cache.',
                    );
                    resolve(hasUnseenInCache);
                    resolved = true;
                }
            }, 2000);

            braze.subscribeToContentCardsUpdates(() => {
                if (resolved) return;
                clearTimeout(fallbackTimeout);

                const updatedCards = braze.getCachedContentCards()
                    .cards as ExtendedCard[];
                const hasUnseen = updatedCards.some(
                    (card) => !card.viewed && !card.dismissed,
                );

                resolve(hasUnseen);
                resolved = true;
            });
        });
    } catch (err) {
        console.error('[Braze] Failed to fetch unseen content cards:', err);
        return false;
    }
}
